import type { LeaderboardEntry, MoversDTO } from "../../shared/types";
import { LeagueHeatmap } from "../components/LeagueHeatmap";
import { ManagerAvatar } from "../components/ManagerAvatar";
import { SectorBattle } from "../components/SectorBattle";
import { TickerTape } from "../components/TickerTape";
import { DirectionTag, Pill, RankBadge } from "../components/ui";
import { managerColor } from "../lib/colors";
import { formatPct, returnColor } from "../lib/format";

// ---- Sample data (preview only; no API) -----------------------------------

interface Mgr {
  name: string;
  seed: number;
  slots: number[];
}

const MANAGERS: Mgr[] = [
  { name: "Amit", seed: 1, slots: [0.41, 0.18, 0.27, 0.06, -0.04, 0.12, 0.21, 0.33, 0.02, -0.22, 0.55, 0.08] },
  { name: "Steve", seed: 2, slots: [0.3, 0.22, 0.11, -0.08, 0.14, 0.06, 0.19, -0.05, 0.27, 0.1, 0.04, -0.12] },
  { name: "Matt", seed: 3, slots: [0.12, -0.06, 0.18, 0.09, 0.03, -0.15, 0.21, 0.07, -0.02, 0.14, 0.05, -0.09] },
  { name: "Kris", seed: 4, slots: [0.05, 0.02, -0.11, 0.08, -0.04, 0.13, -0.07, 0.04, 0.09, -0.02, 0.06, 0.01] },
  { name: "Tannor", seed: 5, slots: [-0.08, 0.04, -0.12, 0.02, -0.19, 0.06, -0.03, 0.07, -0.14, 0.05, -0.06, 0.1] },
  { name: "Jason", seed: 6, slots: [-0.15, -0.04, 0.06, -0.22, -0.08, 0.03, -0.11, -0.18, 0.04, -0.09, -0.02, 0.07] },
];

const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
const RANKED = [...MANAGERS].sort((a, b) => avg(b.slots) - avg(a.slots));

const ENTRIES: LeaderboardEntry[] = RANKED.map((m, i) => ({
  rank: i + 1,
  priorRank: i + 1,
  delta: 0,
  id: m.seed,
  name: m.name,
  seed: m.seed,
  avgReturn: avg(m.slots),
  slotReturns: m.slots,
  sparkline: [],
}));

const P = (ticker: string, ret: number, sector: string, seed: number, dir: "LONG" | "SHORT" = "LONG") => ({
  ticker,
  direction: dir as MoversDTO["positions"][number]["direction"],
  managerId: seed,
  manager: MANAGERS[seed - 1].name,
  seed,
  return: ret,
  sector,
});

const SAMPLE_MOVERS: MoversDTO = {
  positions: [
    P("NVDA", 0.41, "Information Technology", 1),
    P("AVGO", 0.27, "Information Technology", 1),
    P("AMD", 0.18, "Information Technology", 2),
    P("MU", 0.12, "Information Technology", 3),
    P("TXN", 0.06, "Information Technology", 4),
    P("SMCI", -0.06, "Information Technology", 5),
    P("META", 0.14, "Communication", 2),
    P("NFLX", 0.19, "Communication", 3),
    P("GOOGL", 0.09, "Communication", 1),
    P("RDDT", -0.05, "Communication", 4),
    P("GEV", 0.33, "Industrials", 1),
    P("RKLB", 0.21, "Industrials", 2),
    P("FIX", 0.1, "Industrials", 3),
    P("ETN", 0.04, "Industrials", 6),
    P("CELH", 0.13, "Consumer", 4),
    P("UBER", 0.05, "Consumer", 2),
    P("NCLH", -0.07, "Consumer", 5),
    P("PEP", 0.02, "Consumer", 6, "SHORT"),
    P("COIN", 0.44, "Financials", 1),
    P("SOFI", 0.03, "Financials", 3),
    P("V", -0.08, "Financials", 5, "SHORT"),
    P("HOOD", -0.12, "Financials", 6, "SHORT"),
    P("VST", -0.02, "Energy", 4),
    P("TLN", -0.19, "Energy", 5, "SHORT"),
    P("NEE", -0.06, "Energy", 6, "SHORT"),
    P("BE", -0.14, "Energy", 2, "SHORT"),
  ],
};

function heatColor(r: number): string {
  const mag = Math.min(Math.abs(r) / 0.4, 1);
  const a = (0.1 + mag * 0.62).toFixed(2);
  return r >= 0 ? `rgba(22,199,132,${a})` : `rgba(234,57,67,${a})`;
}

// ---- Section frame --------------------------------------------------------

function Demo({ tag, title, blurb, children }: { tag: string; title: string; blurb: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <div className="mb-3 flex items-baseline gap-3">
        <span className="rounded bg-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-accent">
          {tag}
        </span>
        <h2 className="text-lg font-bold text-fg">{title}</h2>
      </div>
      <p className="mb-4 max-w-2xl text-sm text-muted">{blurb}</p>
      {children}
    </section>
  );
}

// ---- ON THE CLOCK bumper (broadcast scene) --------------------------------

function OnTheClockDemo() {
  const m = MANAGERS[1]; // Steve
  const color = managerColor(m.seed);
  return (
    <div
      className="panel relative flex flex-col items-center justify-center overflow-hidden p-10 text-center"
      style={{
        minHeight: 360,
        backgroundImage: `radial-gradient(620px 360px at 50% 18%, ${color}33, transparent 70%)`,
      }}
    >
      <div className="absolute left-5 top-5">
        <Pill tone="live">
          <span className="h-2 w-2 animate-pulseclock rounded-full bg-lossbright" /> ON THE CLOCK
        </Pill>
      </div>
      <div className="absolute right-5 top-5 num text-sm text-muted">PICK 14 / 72 · RD 3</div>

      <ManagerAvatar name={m.name} seed={m.seed} size={120} />
      <div className="mt-5 text-6xl font-black uppercase tracking-tight text-fg" style={{ textShadow: `0 0 40px ${color}66` }}>
        {m.name}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span
          className="num rounded-xl border-2 px-5 py-2 text-4xl font-black"
          style={{ borderColor: color, color, boxShadow: `0 0 24px ${color}44` }}
        >
          0:42
        </span>
      </div>

      <div className="mt-6 flex items-center gap-2 rounded-full bg-gain/15 px-4 py-2">
        <span className="text-sm font-bold uppercase tracking-widest text-gain">Pick is in</span>
        <span className="num text-lg font-bold text-fg">NVDA</span>
        <DirectionTag direction="LONG" />
        <span className="num text-sm text-muted">@ $210.69</span>
      </div>
    </div>
  );
}

// ---- Manager identity layer + standings -----------------------------------

function IdentityDemo() {
  return (
    <div className="panel divide-y divide-edge/60 p-2">
      {RANKED.map((m, i) => {
        const r = avg(m.slots);
        return (
          <div key={m.name} className="flex items-center gap-4 px-3 py-3">
            <RankBadge rank={i + 1} />
            <ManagerAvatar name={m.name} seed={m.seed} size={44} />
            <div className="min-w-0 flex-1">
              <div className="text-base font-bold text-fg">{m.name}</div>
              <div className="text-[11px] uppercase tracking-widest text-faint">seed #{m.seed}</div>
            </div>
            <div className="hidden gap-1 sm:flex">
              {m.slots.map((s, j) => (
                <span
                  key={j}
                  className="h-7 w-2.5 rounded-sm"
                  style={{ backgroundColor: heatColor(s) }}
                  title={`Slot ${j + 1}: ${formatPct(s)}`}
                />
              ))}
            </div>
            <div className={`num w-24 text-right text-2xl font-bold ${returnColor(r)}`}>{formatPct(r)}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Page -----------------------------------------------------------------

export default function PreviewPage() {
  return (
    <div className="mx-auto max-w-5xl py-8">
      <div className="mb-8">
        <div className="mb-1 text-xs font-bold uppercase tracking-widest text-clock">
          Preview · not deployed
        </div>
        <h1 className="text-3xl font-black text-fg">
          Feature <span className="text-brand">glow-up</span> mockups
        </h1>
        <p className="mt-1 text-sm text-muted">
          The ticker, heatmap and sector battle below are the real shipping components fed sample data.
        </p>
      </div>

      <Demo tag="Shipping" title="Live ticker tape" blurb="Goes between the top menu and the hero image on the Season page. Fed by /api/movers.">
        <TickerTape data={SAMPLE_MOVERS} />
      </Demo>

      <Demo tag="Shipping" title="League returns heatmap" blurb="Goes under the standings. Built from each manager's slotReturns in the leaderboard.">
        <LeagueHeatmap entries={ENTRIES} />
      </Demo>

      <Demo tag="Shipping" title="Sector battle board" blurb="Goes under the standings. Aggregates /api/movers by sector with the long/short split.">
        <SectorBattle data={SAMPLE_MOVERS} />
      </Demo>

      <Demo tag="Later" title="ON THE CLOCK bumper" blurb="Signature broadcast moment (not part of this batch).">
        <OnTheClockDemo />
      </Demo>

      <Demo tag="Later" title="Manager portraits + standings" blurb="Identity layer (not part of this batch).">
        <IdentityDemo />
      </Demo>
    </div>
  );
}
